import {
  faAlignCenter,
  faAlignJustify,
  faAlignLeft,
  faAlignRight,
  faBold,
  faHighlighter,
  faItalic,
  faLink,
  faLinkSlash,
  faListUl,
  faStrikethrough,
  faUnderline,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome'
import { Editor } from '@tiptap/react'
import {
  ButtonHTMLAttributes,
  forwardRef,
  ReactNode,
  useRef,
  useState,
} from 'react'
import { twMerge } from 'tailwind-merge'
import { Menu } from '../../Menu'

type Props = { editor: Editor | null; isMobile: boolean }

export const RichTextEditorToolbar = ({ editor, isMobile }: Props) => {
  if (!editor) return null

  const inlineButtons = (
    <>
      <ToolbarButton
        label='Bullet list'
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        icon={faListUl}
      />
      <ToolbarButton
        label='Bold'
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        icon={faBold}
      />
      <ToolbarButton
        label='Italic'
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        icon={faItalic}
      />
      <ToolbarButton
        label='Underline'
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        icon={faUnderline}
      />
      <ToolbarButton
        label='Strikethrough'
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        icon={faStrikethrough}
      />
      <HeadingButton editor={editor} level={1} />
      <HeadingButton editor={editor} level={2} />
      <HeadingButton editor={editor} level={3} />
      <HeadingButton editor={editor} level={4} />
    </>
  )

  const secondaryButtons = (
    <>
      <LinkButton editor={editor} />
      <ToolbarButton
        label='Remove link'
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive('link')}
        icon={faLinkSlash}
      />
      <ToolbarButton
        label='Highlight'
        active={editor.isActive('highlight')}
        onClick={() => editor.chain().focus().toggleHighlight().run()}
        icon={faHighlighter}
      />
      <AlignButton editor={editor} align='left' icon={faAlignLeft} />
      <AlignButton editor={editor} align='center' icon={faAlignCenter} />
      <AlignButton editor={editor} align='justify' icon={faAlignJustify} />
      <AlignButton editor={editor} align='right' icon={faAlignRight} />
    </>
  )

  return (
    <div className='sticky top-0 z-10 flex flex-wrap gap-1 border-b border-base-300 bg-base-50 p-1'>
      {isMobile ? (
        <>
          <Group>{inlineButtons}</Group>
          <Group>{secondaryButtons}</Group>
        </>
      ) : (
        <Group>
          {inlineButtons}
          {secondaryButtons}
        </Group>
      )}
    </div>
  )
}

const Group = ({ children }: { children: ReactNode }) => (
  <div className='flex flex-wrap items-center gap-px rounded border border-base-300 bg-white'>
    {children}
  </div>
)

type ToolbarButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  active?: boolean
  label: string
  icon?: FontAwesomeIconProps['icon']
  children?: ReactNode
}

const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  ({ active, label, icon, children, className, ...props }, ref) => (
    <button
      ref={ref}
      type='button'
      aria-label={label}
      aria-pressed={!!active}
      title={label}
      className={twMerge(
        'flex h-8 min-w-8 items-center justify-center rounded px-2 text-sm text-base-700',
        'hover:bg-base-100 disabled:cursor-not-allowed disabled:opacity-40',
        active && 'bg-primary-100 text-primary-700',
        className,
      )}
      {...props}>
      {icon ? <FontAwesomeIcon icon={icon} /> : children}
    </button>
  ),
)

const HeadingButton = ({
  editor,
  level,
}: {
  editor: Editor
  level: 1 | 2 | 3 | 4
}) => (
  <ToolbarButton
    label={`Heading ${level}`}
    active={editor.isActive('heading', { level })}
    onClick={() => editor.chain().focus().toggleHeading({ level }).run()}>
    <span className='font-semibold'>H{level}</span>
  </ToolbarButton>
)

const AlignButton = ({
  editor,
  align,
  icon,
}: {
  editor: Editor
  align: 'left' | 'center' | 'justify' | 'right'
  icon: FontAwesomeIconProps['icon']
}) => (
  <ToolbarButton
    label={`Align ${align}`}
    active={editor.isActive({ textAlign: align })}
    onClick={() => editor.chain().focus().setTextAlign(align).run()}
    icon={icon}
  />
)

const LinkButton = ({ editor }: { editor: Editor }) => {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState('')

  const openMenu = () => {
    const existing = editor.getAttributes('link').href as string | undefined
    setUrl(existing ?? 'https://')
    setOpen(true)
  }

  const save = () => {
    const trimmed = url.trim()
    if (!trimmed) editor.chain().focus().unsetLink().run()
    else
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: trimmed })
        .run()
    setOpen(false)
  }

  return (
    <>
      <ToolbarButton
        ref={triggerRef}
        label='Add link'
        active={editor.isActive('link')}
        onClick={openMenu}
        icon={faLink}
      />
      <Menu
        isOpen={open}
        onClose={() => setOpen(false)}
        triggerRef={triggerRef}
        position='bottom'
        align='start'
        closeOnScroll={false}>
        <form
          className='flex items-center gap-2 p-1'
          onSubmit={(e) => {
            e.preventDefault()
            save()
          }}>
          <input
            type='url'
            autoFocus
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder='https://example.com'
            className='w-64 rounded border border-base-300 px-2 py-1 text-sm outline-none focus:border-primary-500'
          />
          <button
            type='submit'
            className='rounded bg-primary-600 px-3 py-1 text-sm font-medium text-white hover:bg-primary-700'>
            Save
          </button>
        </form>
      </Menu>
    </>
  )
}
